import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiImage } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LivePreview from './LivePreview';
import StyleSelector from './StyleSelector';
import DownloadManager from './DownloadManager';

/**
 * ImageGenerator - 圖片生成模態框 (全新設計)
 * 
 * 修復：
 * 1. 手機版排序：預覽 -> 風格 -> 下載
 * 2. 簡化佈局，確保清晰可見
 */
const ImageGenerator = ({ 
  language, 
  calculatedDays, 
  relationshipLabel, 
  detailedTime, 
  anniversaryDate, 
  anniversaryType, 
  quote,
  isOpen,
  onClose 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('original');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // 模態框狀態管理
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setIsDownloading(false);
      setDownloadSuccess(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const handleDownloadStart = () => {
    setIsDownloading(true);
    setDownloadSuccess(false);
  };

  const handleDownloadComplete = (success) => {
    setIsDownloading(false);
    setDownloadSuccess(success);
    if (success) {
      setTimeout(() => setDownloadSuccess(false), 3000);
    }
  };

  // 預覽組件的 Props
  const previewProps = {
    template: selectedTemplate,
    language,
    calculatedDays,
    relationshipLabel,
    detailedTime,
    anniversaryDate,
    anniversaryType,
    quote
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-6xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh]"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 標題欄 */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 p-4 sm:p-6 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                  <SafeIcon icon={FiImage} className="text-love-500" />
                  {language === 'zh' ? '生成圖片' : 'Generate Image'}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {language === 'zh' ? '選擇風格，下載分享' : 'Choose style and download'}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {language === 'zh' ? '最後更新：' : 'Last updated: '}
                  <time dateTime="2026-05-01">{language === 'zh' ? '2026 年 5 月 1 日' : 'May 1, 2026'}</time>
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl sm:text-2xl" />
              </button>
            </div>

            {/* 主要內容區 - 可滾動 */}
            <div className="p-4 sm:p-8 bg-white dark:bg-gray-800 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
                
                {/* 手機：預覽第一，電腦：預覽在左 */}
                <div className="order-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 sm:mb-4 flex items-center">
                    <span className="bg-love-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
                    {language === 'zh' ? '即時預覽' : 'Preview'}
                  </h3>
                  
                  {/* 預覽容器 */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-6 sm:p-8 flex justify-center items-center min-h-[380px] sm:min-h-[400px]">
                    <LivePreview {...previewProps} />
                  </div>
                </div>

                {/* 手機：控制面板第二，電腦：控制面板在右 */}
                <div className="order-2 space-y-6 sm:space-y-8">
                  
                  {/* 風格選擇 */}
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 sm:mb-4 flex items-center">
                      <span className="bg-love-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
                      {language === 'zh' ? '選擇風格' : 'Style'}
                    </h3>
                    
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-2xl p-4 sm:p-6">
                      <StyleSelector
                        selectedTemplate={selectedTemplate}
                        onTemplateChange={handleTemplateChange}
                        language={language}
                      />
                    </div>
                  </div>

                  {/* 下載區域 */}
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 sm:mb-4 flex items-center">
                      <span className="bg-love-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">3</span>
                      {language === 'zh' ? '下載圖片' : 'Download'}
                    </h3>
                    
                    <DownloadManager
                      language={language}
                      template={selectedTemplate}
                      calculatedDays={calculatedDays}
                      relationshipLabel={relationshipLabel}
                      detailedTime={detailedTime}
                      anniversaryDate={anniversaryDate}
                      anniversaryType={anniversaryType}
                      quote={quote}
                      isDownloading={isDownloading}
                      downloadSuccess={downloadSuccess}
                      onDownloadStart={handleDownloadStart}
                      onDownloadComplete={handleDownloadComplete}
                    />
                  </div>
                </div>

              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageGenerator;